import { z } from "zod";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { capitalize } from "@mui/material";

export const TABLE_SELECTION_PARAM_KEY = "selection";
export const TABLE_SELECTION_SEPARATOR = ",";

const tableSelectionSchema = z.array(z.string());

export const useTableSelectionParam = (prefix?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const key = prefix
    ? prefix + capitalize(TABLE_SELECTION_PARAM_KEY)
    : TABLE_SELECTION_PARAM_KEY;

  const getSelectionParam = () => {
    const param = searchParams.get(key) || "";
    const result = param.split(TABLE_SELECTION_SEPARATOR);

    const validation = tableSelectionSchema.safeParse(result);
    if (!validation.success) {
      throw new Error("Invalid selection param:", validation.error);
    }

    return result.filter((x) => x);
  };

  const updateSelectionParam = useCallback(
    (selection: string[]) => {
      if (selection.length === 0) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, selection.join(TABLE_SELECTION_SEPARATOR));
      }
      setSearchParams(searchParams, { replace: true });
    },
    [key, searchParams, setSearchParams]
  );

  const clearSelection = useCallback(
    (prefix?: string) => {
      searchParams.delete(
        prefix
          ? prefix + capitalize(TABLE_SELECTION_PARAM_KEY)
          : TABLE_SELECTION_PARAM_KEY
      );
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return {
    selection: getSelectionParam(),
    setSelection: updateSelectionParam,
    clearSelection,
  } as const;
};
